import { PartnerContactDto, PartnerDto, PartnerTypeEnum } from './../../service/dto';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NzUploadFile, NzUploadXHRArgs } from 'ng-zorro-antd/upload';
import { HttpPartnerService } from '../../service/http-partner.service';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, debounceTime, distinctUntilChanged, map, of, Subscription, switchMap } from 'rxjs';
import { NzMessageService } from 'ng-zorro-antd/message';




export function uniqueFieldValidator(partnerService: HttpPartnerService, field: string, partner?: PartnerDto): AsyncValidatorFn {
	return (control: AbstractControl) => {
		if (!control.value) {
			return of(null);
		}
		return of(control.value).pipe(
			debounceTime(500),
			distinctUntilChanged(),
			switchMap(value =>
				partnerService.validate(field, value, partner).pipe(
					map(result => {
						if (result.valid == true) {
							return null;
						}
						if (result.reason == 'NotUnique') {
							return { notUnique: true };
						}
						return { invalid: true };
					}),
					catchError(() => of(null))
				)
			)
		);
	};
}

@Component({
	selector: 'app-partner-edit',
	standalone: false,
	templateUrl: './partner-edit.component.html',
	styleUrl: './partner-edit.component.scss'
})
export class PartnerEditComponent implements OnInit {
	partnerId?: number;
	partnerForm!: FormGroup;

	partnerTypes: PartnerTypeEnum[] = ['MobileNetworkOperator', 'Other'];
	imagePreview: string | ArrayBuffer | null = null;
	loading = false;
	avatarUrl?: string;
	uploadProgress = 0;
	submitting = false;
	subscription = new Subscription();
	mode: 'New' | 'Edit' = 'New';

	constructor(private fb: FormBuilder, private router: Router, private partnerService: HttpPartnerService, private activatedRoute: ActivatedRoute, private messageService: NzMessageService) {
	}
	ngOnInit(): void {
		this.subscription.add(
			this.activatedRoute.paramMap.subscribe(params => {
				const partnerId = params.get('partnerId');
				if (partnerId) {
					this.mode = 'Edit';
					this.partnerService.get(+partnerId).subscribe(result => {
						this.partnerId = result.id;
						const contacts = result.contacts.map((contact: any) => (this.fb.group({
							id: contact.id,
							createdAt: contact.createdAt,
							fullName: [contact.fullName, Validators.required],
							emailAddress: [contact.emailAddress, Validators.required],
							msisdn: [contact.msisdn, Validators.required],
							jobRole: [contact.jobRole, Validators.required]
						})));

						this.partnerForm = this.fb.group({
							name: [result.name, [Validators.required, Validators.pattern('^[a-zA-Z0-9][a-zA-Z0-9 _-]+$')], [uniqueFieldValidator(this.partnerService, 'name', result)]],
							partnerType: [result.partnerType, Validators.required],
							registrationNumber: [result.registrationNumber, [Validators.required, Validators.pattern('^[a-zA-Z0-9][a-zA-Z0-9 _-]+$')], [uniqueFieldValidator(this.partnerService, 'registrationNumber', result)]],
							address: [result.address, Validators.required],
							tenanted: [result.tenanted],
							iccidLength: [result.iccidLength],
							iccidPattern: [result.iccidPattern],
							contacts: this.fb.array(contacts)
						});
						if (result.image?.imageData) {
							const base64Image = `data:image/png;base64,${result.image.imageData}`;
							this.imagePreview = base64Image;
							this.avatarUrl = base64Image;
						}
					});
				} else {
					this.mode = 'New';
					this.partnerForm = this.fb.group({
						name: ['', [Validators.required, Validators.pattern('^[a-zA-Z0-9][a-zA-Z0-9 _-]+$')], [uniqueFieldValidator(this.partnerService, 'name')]],
						partnerType: [null, Validators.required],
						registrationNumber: ['', [Validators.required, Validators.pattern('^[a-zA-Z0-9][a-zA-Z0-9 _-]+$')], [uniqueFieldValidator(this.partnerService, 'registrationNumber')]],
						address: ['', Validators.required],
						tenanted: [false],
						iccidLength: [null],
						iccidPattern: [''],
						contact: this.fb.group({
							fullName: ['', Validators.required],
							emailAddress: ['', [Validators.required, Validators.email]],
							msisdn: ['', Validators.required],
							jobRole: ['']
						})
					});
				}
			})
		);
	}


	handleChange(info: { file: NzUploadFile }): void {
		switch (info.file.status) {
			case 'uploading':
				this.loading = true;
				break;
			case 'done':
				// Get this url from response in real world.
				this.getBase64(info.file!.originFileObj!, (img: string) => {
					this.loading = false;
					this.avatarUrl = img;
				});
				break;
			case 'error':
				this.loading = false;
				break;
		}
	}
	private getBase64(img: File, callback: (img: string) => void): void {
		const reader = new FileReader();
		reader.addEventListener('load', () => callback(reader.result!.toString()));
		reader.readAsDataURL(img);
	}

	beforeUpload = (file: NzUploadFile, fileList: NzUploadFile[]): boolean => {
		if (!file.type!.startsWith('image/')) {
			return false;
		}
		const reader = new FileReader();
		reader.onload = () => {
			this.imagePreview = reader.result;
		};
		reader.readAsDataURL(file as any as File);
		return true;
	}
	customUpload = (item: NzUploadXHRArgs): any => { }

	get contacts(): FormArray {
		return this.partnerForm.get('contacts') as FormArray;
	}
	addContact(): void {
		this.contacts.push(this.fb.group({
			fullName: ['', Validators.required],
			emailAddress: ['', [Validators.required, Validators.email]],
			msisdn: ['', Validators.required],
			jobRole: ['']
		}));
	}
	removeContact(index: number): void {
		this.contacts.removeAt(index);
	}

	doSubmit() {
		let contacts: PartnerContactDto[] = [];
		if (this.mode == 'New') {
			const firstContact = this.partnerForm.get('contact')!.value;
			contacts.push({
				id: 0,
				createdAt: new Date(),
				fullName: firstContact.fullName,
				emailAddress: firstContact.emailAddress,
				msisdn: firstContact.msisdn,
				jobRole: firstContact.jobRole,
				hashCode: 0
			});
		} else {
			const array = this.partnerForm.get('contacts') as FormArray
			contacts = array.value.map((contact: any) => ({
				id: contact.id || 0,
				createdAt: contact.createdAt || new Date(),
				fullName: contact.fullName,
				emailAddress: contact.emailAddress,
				msisdn: contact.msisdn,
				jobRole: contact.jobRole,
				hashCode: 0
			}));
		}

		const payload: PartnerDto = {
			id: 0,
			createdAt: new Date(),
			name: this.partnerForm.get('name')?.value,
			partnerType: this.partnerForm.get('partnerType')?.value,
			registrationNumber: this.partnerForm.get('registrationNumber')?.value,
			address: this.partnerForm.get('address')?.value,
			tenanted: this.partnerForm.get('tenanted')?.value,
			iccidLength: this.partnerForm.get('iccidLength')?.value,
			iccidPattern: this.partnerForm.get('iccidPattern')?.value,
			contacts: contacts,
			image: {
				id: 0,
				createdAt: new Date(),
				imageType: 'PartnerLogo',
				imageData: this.imagePreview as string,
				hashCode: 0
			},
			hashCode: 0
		};
		if (this.mode == 'New') {
			this.partnerService.create(payload).subscribe(result => {
				this.messageService.info('Partner created');
				this.router.navigate(['/partner', 'view', result.id]);
			});
		} else {
			this.partnerService.edit(this.partnerId!, payload).subscribe(result => {
				this.messageService.info('Partner updated');
				this.router.navigate(['/partner', 'view', result.id]);
			});
		}
	}

	private getFormControl(form: AbstractControl|FormGroup, name: string): AbstractControl|null{
		if(name.indexOf(".") == -1){
			return form.get(name);
		}
		const [first, ...rest] = name.split(".");


		const nextControl = (form as FormGroup).get(first);
		if (nextControl) {
			return this.getFormControl(nextControl, rest.join("."));
		}
		return nextControl;
	}

	controlInvalid(name: string): boolean {
		const ctrl = this.getFormControl(this.partnerForm,name);
		return !!ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched);
	}
	getErrors(name: string) {
		return this.getFormControl(this.partnerForm,name)?.errors;
	}
	getError(name: string): string | null {
		const ctrl = this.getFormControl(this.partnerForm,name);
		if (!ctrl || !ctrl.errors) return null;

		if (ctrl.errors['required']) return 'This field is required';
		if (ctrl.errors['email']) return 'Invalid email format';
		if (ctrl.errors['pattern']) return 'Invalid format';
		if (ctrl.errors['notUnique']) return 'Already in use';
		if (ctrl.errors['invalidDomain']) return 'Invalid domain';
		if (ctrl.errors['InvalidMsisdn']) return 'Invalid format';
		if (ctrl.errors['invalid']) return 'Invalid';
		if (ctrl.errors['notFound']) return 'Document not found';
		if (ctrl.errors['idAlreadyUsed']) return 'Already Used by another Agent';

		return 'Invalid value';
	}
	getFormValidationErrors(form: FormGroup | FormArray): any {
		const result: any = {};

		Object.keys(form.controls).forEach(key => {
			const control = form.get(key);
			if (control instanceof FormGroup || control instanceof FormArray) {
				const childErrors = this.getFormValidationErrors(control);
				if (Object.keys(childErrors).length > 0) {
					result[key] = childErrors;
				}
			} else if (control && control.errors) {
				result[key] = control.errors;
			}
		});

		return result;
	}
}
